import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, combineLatest, map, of, switchMap, tap } from 'rxjs';
import { AccessControl, Reports } from '@pncl/common-models';
import { CustomAttribute, User } from '../models/user';
import {
  AttributesSearchFilter,
  DateRangeFilter,
  DefaultFilterKey,
  FilterData,
  FilterType,
  OwnerFilter,
  ProgramSearchFilter,
  RoleFilter,
  SiteSearchFilter,
  SpaceFilter,
  UserSearchFilter,
  ArchivedFilter,
  UserSearchFilterKey,
  FilterKey,
  FilterKeyValuePair,
  EventsAtSameTimeFilter,
  FileTypeFilter,
  HasCallsFilter,
  HasRecordingFilter,
} from '../standalones/components/generic-filters-view/filters.interface';
import { institutionHasSites } from '../utilities/sites.utils';
import { Event } from '../models/event';
import { PersonasService } from './personas.service';
import { AclService } from './acl.service';
import { UserService } from './user.service';
import { CapabilityTierService } from './capability-tier.service';
import { ProgramService } from './program.service';
import { notifyLinkedFilters } from './filters.service';

/** Personas a coaching report may be filtered to. */
const COACHING_HOST_PERSONA_SET = new Set<string>(Reports.COACHING_HOST_PERSONAS);

const DEFAULT_COACHING_HOST_ROLE_FILTER = Reports.DEFAULT_COACHING_HOST_PERSONAS.map((persona) => ({
  _id: persona,
}));

@Injectable({
  providedIn: 'root',
})
export class FiltersFactoryService {
  private isSitesEnabled = false;
  private user?: User;

  constructor(
    private translateService: TranslateService,
    private userService: UserService,
    private aclService: AclService,
    private capabilityTierService: CapabilityTierService,
    private personasService: PersonasService,
    private programService: ProgramService,
  ) {
    this.userService.user.subscribe((res) => {
      this.user = res?.user;
      this.isSitesEnabled = institutionHasSites(this.user?.institution);
    });
  }

  createSpacesFilters(): Observable<FilterData[]> {
    return combineLatest([
      this.createCreatedDateFilter(),
      this.createUpdatedDateFilter(),
      this.createOwnerFilter(),
      this.createArchivedStatusFilter(),
      this.createHostSearchFilter(),
      this.createParticipantSearchFilter(),
      this.createSiteSearchFilter(),
      this.createProgramSearchFilter(),
      this.createAttributesSearchFilters(),
    ]).pipe(map((filterDataArr) => this.expandNestedFilterDataArr(filterDataArr)));
  }

  createEventsFilters(): Observable<FilterData[]> {
    return combineLatest([
      this.createSiteSearchFilter(),
      this.createProgramSearchFilter(),
      this.createEventOrganizersSearchFilter(),
      this.createEventAttendeesSearchFilter(),
      this.createAttributesSearchFilters(),
    ]).pipe(map((filterDataArr) => this.expandNestedFilterDataArr(filterDataArr)));
  }

  createReassignableEventsFilters(
    event: Event,
    initialValues: FilterKeyValuePair[],
  ): Observable<FilterData[]> {
    return combineLatest([
      this.createEventsAtSameTimeFilter(event),
      this.createEventsFilters(),
    ]).pipe(
      map((filters) => this.expandNestedFilterDataArr(filters)),
      tap((filters) => this.setInitialFilterValues(filters, initialValues)),
      tap((filters) =>
        this.lockFilters(filters, [
          DefaultFilterKey.EventsAtSameTimeFilter,
          DefaultFilterKey.SitesFilter,
        ]),
      ),
    );
  }

  createAttendanceReportsFilters(): Observable<FilterData[]> {
    return combineLatest([
      this.createRolesFilter(),
      this.createSiteSearchFilter(),
      this.createAttributesSearchFilters(),
    ]).pipe(map((filterDataArr) => this.expandNestedFilterDataArr(filterDataArr)));
  }

  createSiteAnalyticsFilters(): Observable<FilterData[]> {
    return combineLatest([
      this.createRolesFilter(), // No default selection - will be handled via query params
      this.createSiteSearchFilter(),
    ]).pipe(map((filterDataArr) => this.expandNestedFilterDataArr(filterDataArr)));
  }

  createSiteCoachingFilters(): Observable<FilterData[]> {
    return combineLatest([
      this.createRolesFilter(COACHING_HOST_PERSONA_SET),
      this.createSiteSearchFilter(),
    ]).pipe(
      map((filterDataArr) => this.expandNestedFilterDataArr(filterDataArr)),
      tap((filters) =>
        this.setInitialFilterValues(filters, [
          { key: DefaultFilterKey.RolesFilter, value: DEFAULT_COACHING_HOST_ROLE_FILTER },
        ]),
      ),
    );
  }

  createAnalyticsReportsFilters(): Observable<FilterData[]> {
    return combineLatest([
      this.createRolesFilter(),
      this.createSiteSearchFilter(),
      this.createAttributesSearchFilters(),
    ]).pipe(map((filterDataArr) => this.expandNestedFilterDataArr(filterDataArr)));
  }

  createTeacherReportsFilters(): Observable<FilterData[]> {
    return this.createSiteSearchFilter().pipe(
      map((filters) => this.filtersWithMultipleOptions(filters)),
    );
  }

  createInstitutionTeachersReportFilters(): Observable<FilterData[]> {
    return combineLatest([
      this.createRolesFilter(COACHING_HOST_PERSONA_SET),
      this.createTeacherReportsFilters(),
    ]).pipe(
      map((filterDataArr) => this.expandNestedFilterDataArr(filterDataArr)),
      tap((filters) =>
        this.setInitialFilterValues(filters, [
          { key: DefaultFilterKey.RolesFilter, value: DEFAULT_COACHING_HOST_ROLE_FILTER },
        ]),
      ),
    );
  }

  createRecordingsReportsFilters(): Observable<FilterData[]> {
    return combineLatest([this.createSpacesFilter(), this.createUserSearchFilter()]).pipe(
      map((filterDataArr) => this.expandNestedFilterDataArr(filterDataArr)),
    );
  }

  createFilesFilters(): Observable<FilterData[]> {
    return combineLatest([this.createUploadedDateFilter(), this.createFileTypeFilter()]).pipe(
      map((filterDataArr) => this.expandNestedFilterDataArr(filterDataArr)),
    );
  }

  createAnalyticsSessionsFilters(): Observable<FilterData[]> {
    return combineLatest([
      this.createHasCallsFilter(),
      this.createHasRecordingFilter(),
      this.createSpacesFilter(),
      this.createUserSearchFilter(
        DefaultFilterKey.EventAttendeesFilter,
        FilterType.UserSearch,
        this.translateService.instant('ATTENDEES'),
      ),
    ]).pipe(
      map((filterDataArr) => this.expandNestedFilterDataArr(filterDataArr)),
      tap((filters) =>
        this.setInitialFilterValues(filters, [
          { key: DefaultFilterKey.HasCallsFilter, value: true },
        ]),
      ),
      tap((filters) => this.lockFilters(filters, [DefaultFilterKey.HasCallsFilter])),
    );
  }

  //
  // Private Functions
  //

  private createCreatedDateFilter(): Observable<DateRangeFilter> {
    return this.createDateFilter(
      DefaultFilterKey.DateCreatedFilter,
      this.translateService.instant('DATE CREATED'),
    );
  }

  private createUpdatedDateFilter(): Observable<DateRangeFilter> {
    return this.createDateFilter(
      DefaultFilterKey.DateUpdatedFilter,
      this.translateService.instant('DATE LAST UPDATED'),
    );
  }

  private createUploadedDateFilter(): Observable<DateRangeFilter> {
    return this.createDateFilter(
      DefaultFilterKey.DateCreatedFilter,
      this.translateService.instant('DATE UPLOADED'),
    );
  }

  private createOwnerFilter(): Observable<OwnerFilter> {
    const userData = this.userService.user.getValue();
    if (!userData?.user) {
      throw new Error('User data is missing');
    }

    return of(
      new OwnerFilter(
        this.translateService,
        this.translateService.instant('IS OWNER'),
        userData.user._id,
        this.translateService.instant('Only show Spaces you own'),
      ),
    );
  }

  private createHostSearchFilter(): Observable<UserSearchFilter> {
    return this.createUserSearchFilter(
      DefaultFilterKey.HostsFilter,
      FilterType.UserSearch,
      this.translateService.instant('HOSTS'),
    );
  }

  private createParticipantSearchFilter(): Observable<UserSearchFilter> {
    return this.createUserSearchFilter(
      DefaultFilterKey.ParticipantsFilter,
      FilterType.UserSearch,
      this.translateService.instant('PARTICIPANTS'),
    );
  }

  private createEventOrganizersSearchFilter(): Observable<UserSearchFilter> {
    return this.createUserSearchFilter(
      DefaultFilterKey.EventOrganizersFilter,
      FilterType.UserSearch,
      this.translateService.instant('ORGANIZERS'),
    );
  }

  private createEventAttendeesSearchFilter(): Observable<UserSearchFilter> {
    return this.createUserSearchFilter(
      DefaultFilterKey.EventAttendeesFilter,
      FilterType.UserSearch,
      this.translateService.instant('ATTENDEES'),
    );
  }

  private createDateFilter(
    key: DefaultFilterKey.DateCreatedFilter | DefaultFilterKey.DateUpdatedFilter,
    title: string,
  ) {
    return of(new DateRangeFilter(key, this.translateService, title));
  }

  private createUserSearchFilter(
    key: UserSearchFilterKey = DefaultFilterKey.UsersFilter,
    type: FilterType = FilterType.UserSearch,
    title: string = this.translateService.instant('PEOPLE'),
  ) {
    return of(new UserSearchFilter(key, type, this.translateService, title));
  }

  createProfilesFilters(initialFilters: FilterKeyValuePair[] = []): Observable<FilterData[]> {
    return combineLatest([
      this.createRolesFilter(),
      this.createSiteSearchFilter(),
      this.createAttributesSearchFilters(),
    ]).pipe(
      map(([roleFilters, siteFilterArr, attributesFiltersArr]) => [
        roleFilters,
        ...siteFilterArr,
        ...attributesFiltersArr,
      ]),
      tap((filters) => this.setInitialFilterValues(filters, initialFilters)),
    );
  }

  createAvailabilityFilters(): Observable<FilterData[]> {
    return this.createProfilesFilters().pipe(
      map((filters) => filters.filter((filter) => filter.key !== DefaultFilterKey.RolesFilter)),
    );
  }

  private createSiteSearchFilter(): Observable<SiteSearchFilter[]> {
    return of(
      this.isSitesEnabled && this.user
        ? [
            new SiteSearchFilter(
              this.translateService,
              this.translateService.instant('SITE'),
              [],
              this.aclService.getUserSites(this.user),
            ),
          ]
        : [],
    );
  }

  /**
   * Creates the search filters for every custom attribute associated with the user's institution.
   * Uses the CUSTOM_ATTRIBUTES capability to determine if custom attributes should be shown.
   * @returns An observable that emits the custom attributes search filters.
   */
  private createAttributesSearchFilters(): Observable<AttributesSearchFilter[]> {
    return combineLatest([
      this.capabilityTierService.shouldEnableCapability$(
        AccessControl.Capability.CUSTOM_ATTRIBUTES,
      ),
      this.userService.allInstitutionCustomAttributes,
    ]).pipe(
      map(([isCustomAttributesEnabled, custom_attributes]) =>
        isCustomAttributesEnabled && custom_attributes
          ? custom_attributes.map(
              (attribute: CustomAttribute) =>
                new AttributesSearchFilter(this.translateService, attribute),
            )
          : [],
      ),
    );
  }

  private createRolesFilter(allowedPersonas?: ReadonlySet<string>): Observable<RoleFilter> {
    const personas = this.personasService
      .getInstitutionPersonasList(this.user?.institution)
      .filter((persona) => !allowedPersonas || allowedPersonas.has(persona._id));

    return of(
      new RoleFilter(this.translateService, this.translateService.instant('ROLES'), [], personas),
    );
  }

  private createArchivedStatusFilter(): Observable<ArchivedFilter> {
    return of(
      new ArchivedFilter(
        this.translateService,
        this.translateService.instant('Is Archived'),
        this.translateService.instant('Only show archived Spaces'),
      ),
    );
  }

  private createHasCallsFilter(): Observable<HasCallsFilter> {
    return of(
      new HasCallsFilter(
        this.translateService,
        this.translateService.instant('Has Calls'),
        this.translateService.instant('Show sessions with calls'),
      ),
    );
  }

  private createHasRecordingFilter(): Observable<HasRecordingFilter> {
    return of(
      new HasRecordingFilter(
        this.translateService,
        this.translateService.instant('Recordings'),
        this.translateService.instant('Show sessions with recordings'),
      ),
    );
  }

  private createSpacesFilter(): Observable<SpaceFilter> {
    return of(new SpaceFilter(this.translateService, this.translateService.instant('SPACES'), []));
  }

  private createFileTypeFilter(): Observable<FileTypeFilter> {
    return of(new FileTypeFilter(this.translateService, this.translateService.instant('TYPE')));
  }

  private createProgramSearchFilter(): Observable<ProgramSearchFilter[]> {
    return this.capabilityTierService
      .shouldEnableCapability$(AccessControl.Capability.PROGRAMS)
      .pipe(
        switchMap((isProgramsEnabled) =>
          isProgramsEnabled
            ? this.programService
                .getPrograms()
                .pipe(
                  map((programs) => [
                    new ProgramSearchFilter(
                      this.translateService,
                      this.translateService.instant('PROGRAMS'),
                      [],
                      programs,
                    ),
                  ]),
                )
            : of([]),
        ),
      );
  }

  private createEventsAtSameTimeFilter(event: Event): Observable<EventsAtSameTimeFilter> {
    return of(
      new EventsAtSameTimeFilter(
        this.translateService,
        this.translateService.instant('HAPPENING AT THE SAME TIME'),
        this.translateService.instant('Only show events happening at the same time'),
        event,
      ),
    );
  }

  private lockFilters(filters: FilterData[], filterKeys: FilterKey[]): void {
    filterKeys.forEach((filterKey) => {
      const filterData = filters.find((filter) => filter.key === filterKey);
      if (filterData) {
        filterData.isLocked = true;
      }
    });
  }

  private setInitialFilterValues(filters: FilterData[], keyValuePairs: FilterKeyValuePair[]): void {
    keyValuePairs.forEach((keyValuePair) => {
      const filterData = filters.find((filter) => filter.key === keyValuePair.key);
      if (!filterData) {
        return;
      }

      const value = filterData.resolveValue(keyValuePair.value);
      filterData.handleFilterSelection(value);
      notifyLinkedFilters(filters, filterData);
    });
  }

  // A viewer with one site is already scoped to it, and a viewer with none has nothing to pick,
  // so in both cases the control cannot change the results.
  private filtersWithMultipleOptions(filters: SiteSearchFilter[]): SiteSearchFilter[] {
    return filters.filter((filter) => filter.allList.length > 1);
  }

  private expandNestedFilterDataArr(filterDataArr: (FilterData | FilterData[])[]): FilterData[] {
    return filterDataArr.flatMap((filterData) =>
      Array.isArray(filterData) ? filterData : [filterData],
    );
  }
}
